import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useEffect, useState } from 'react';

export type CognitoAuthStatus = 'configuring' | 'authenticated' | 'unauthenticated';

/**
 * Amplify sign-in state without `@aws-amplify/ui-react`: the initial status
 * comes from `getCurrentUser()`, then the auth Hub keeps it current. Logs every
 * event and status change.
 *
 * `tokenRefresh_failure` only signs out on non-network errors (parity with
 * ui-react's default handler): a refresh that failed because the device is
 * offline still has a valid session to come back to.
 *
 * `signInWithRedirect` and its failure end a sign-in through Cognito's hosted
 * UI (`useSquareSignIn`).
 */
export const useCognitoAuthStatus = (): CognitoAuthStatus => {
  const [status, setStatus] = useState<CognitoAuthStatus>('configuring');

  useEffect(() => {
    let cancelled = false; // StrictMode double-invokes effects
    getCurrentUser()
      .then(() => !cancelled && setStatus('authenticated'))
      .catch((error: unknown) => {
        if (cancelled) return;
        if (!(error instanceof Error && error.name === 'UserUnAuthenticatedException')) {
          console.error('[auth:cognito] getCurrentUser failed:', error);
        }
        setStatus('unauthenticated');
      });

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log(`[auth:cognito] Event: ${payload.event}`);
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
          setStatus('authenticated');
          break;
        case 'signedOut':
        case 'signInWithRedirect_failure':
          setStatus('unauthenticated');
          break;
        case 'tokenRefresh_failure':
          if (payload.data.error?.name !== 'NetworkError') setStatus('unauthenticated');
          break;
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    console.log(`[auth:cognito]: ${status}`);
  }, [status]);

  return status;
};
